import { animate, spring, transform } from "motion";
import { $eg, EventGroup } from "./EventGroup";

class StarHover {
  constructor() {
    this.elements = document.querySelectorAll("[data-star-hover]");
    this.defaultColors = [
      "hsl(24, 98%, 44%)",
      "hsl(53, 93%, 53%)",
      "hsl(356, 95%, 60%)",
      "hsl(163, 75%, 39%)",
      "hsl(333, 57%, 60%)",
      "hsl(198, 77%, 51%)",
      "hsl(276, 65%, 65%)",
      "hsl(230, 96%, 60%)",
      "hsl(19, 74%, 61%)",
    ];
    this.starSizes = { small: 16, medium: 24, large: 32 };
    this.init();
  }

  /**
   * Initializes the star hover effect on the elements.
   * This method sets up event listeners for each element that triggers a star burst animation
   * when the mouse enters the element. The number of stars and their colors can be customized
   * through data attributes on the elements.
   *
   * @throws {Error} Throws an error if the element's dataset is not properly formatted.
   *
   * @example
   * // Assuming `this.elements` contains a list of DOM elements with appropriate data attributes
   * instance.init();
   */
  init() {
    this.elements.forEach((element) => {
      const count = parseInt(element.dataset.starHoverCount) || 4;
      const colors = element.dataset.starHoverColors
        ? element.dataset.starHoverColors.split(",").map((c) => c.trim())
        : this.defaultColors;

      $eg.off(element, "mouseenter");
      $eg.on(element, "mouseenter", () => {
        this.createStarBurst(element, count, colors);
      });

      $eg.off(element, "mouseleave");
      $eg.on(element, "mouseleave", () => {
        this.removeStarBurst(element);
      });
    });
  }

  /**
   * Retrieves a random size from the available star sizes.
   *
   * This method accesses the `starSizes` property of the class instance,
   * which should be an object containing various size values. It then
   * randomly selects one of these sizes and returns it.
   *
   * @returns {number} A randomly selected size from the available star sizes.
   *
   * @throws {Error} Throws an error if `starSizes` is not defined or is not an object.
   *
   * @example
   * const randomSize = instance.getRandomSize();
   * console.log(randomSize); // Outputs a random size value.
   */
  getRandomSize() {
    const sizes = Object.values(this.starSizes);
    return sizes[Math.floor(Math.random() * sizes.length)];
  }

  /**
   * Creates a star-shaped SVG element.
   *
   * @returns {HTMLElement} The star SVG element with applied styles.
   */
  createStar(color) {
    const size = this.getRandomSize();
    const star = document.createElementNS("http://www.w3.org/2000/svg", "svg");

    star.setAttribute("width", size);
    star.setAttribute("height", size);
    star.setAttribute("viewBox", "0 0 35 35");
    star.setAttribute("fill", "none");
    star.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    star.setAttribute("class", "animated-star");

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute(
      "d",
      "M17.5431 0H17.4838C17.2264 4.56599 15.2989 8.87526 12.0715 12.0996C8.84421 15.3239 4.54492 17.2357 0 17.4675L0 17.5162C4.5481 17.7483 8.8502 19.6622 12.0788 22.8898C15.3074 26.1174 17.2344 30.4307 17.4892 35H17.5431C17.7972 30.436 19.7197 26.127 22.9416 22.9002C26.1634 19.6734 30.4576 17.7561 35 17.5162V17.4675C30.4606 17.2268 26.1692 15.3114 22.948 12.0882C19.7268 8.86506 17.8022 4.56075 17.5431 0Z",
    );

    // You can customize the fill color here
    path.setAttribute("fill", color ?? "#FFE84C");

    star.appendChild(path);

    star.style.cssText = `
      position: absolute;
      pointer-events: none;
      will-change: auto;
    `;

    return star;
  }

  /**
   * Generates a random position on the edge of a given rectangle.
   *
   * This function randomly selects one of the four edges of the rectangle
   * and calculates a position on that edge. The edges are defined as follows:
   * - Top edge (edge 0)
   * - Right edge (edge 1)
   * - Bottom edge (edge 2)
   * - Left edge (edge 3)
   *
   * @param {Object} rect - The rectangle from which to derive the edge position.
   * @param {number} rect.width - The width of the rectangle.
   * @param {number} rect.height - The height of the rectangle.
   * @returns {Object} An object containing the randomly generated position and the edge number.
   * @returns {Object} return.position - The position on the edge with x and y coordinates.
   * @returns {number} return.edge - The edge number (0-3) representing which edge was selected.
   *
   * @example
   * const rect = { width: 100, height: 50 };
   * const result = getRandomEdgePosition(rect);
   * console.log(result); // { position: { x: ..., y: ... }, edge: 0 | 1 | 2 | 3 }
   */
  getRandomEdgePosition(rect) {
    const edge = Math.floor(Math.random() * 4);
    const position = { x: 0, y: 0 };

    switch (edge) {
      case 0:
        position.x = Math.random() * rect.width;
        position.y = 0;
        break;
      case 1:
        position.x = rect.width;
        position.y = Math.random() * rect.height;
        break;
      case 2:
        position.x = Math.random() * rect.width;
        position.y = rect.height;
        break;
      case 3:
        position.x = 0;
        position.y = Math.random() * rect.height;
        break;
    }

    return { position, edge };
  }

  /**
   * Calculates a random end position based on the specified edge, rectangle dimensions, and distance.
   *
   * This function generates a random angle and combines it with a base angle determined by the specified edge.
   * The resulting angle is then used to compute the x and y coordinates of the end position, which are returned as an object.
   *
   * @param {number} edge - The edge from which to calculate the end position.
   *                        Acceptable values are:
   *                        0: Top edge,
   *                        1: Right edge,
   *                        2: Bottom edge,
   *                        3: Left edge.
   * @param {Object} rect - The rectangle object containing dimensions (not used in calculations).
   * @param {number} distance - The distance from the edge to the calculated end position.
   * @returns {{x: number, y: number}} An object containing the x and y coordinates of the calculated end position.
   *
   * @example
   * const position = getRandomEndPosition(1, { width: 100, height: 100 }, 50);
   * console.log(position); // { x: ..., y: ... }
   *
   * @throws {Error} Throws an error if the edge parameter is not between 0 and 3.
   */
  getRandomEndPosition(edge) {
    const distance = 0 + Math.random() * 50;
    const angle = (Math.random() * Math.PI) / 2;
    let baseAngle;

    switch (edge) {
      case 0:
        baseAngle = -Math.PI / 2;
        break;
      case 1:
        baseAngle = 0;
        break;
      case 2:
        baseAngle = Math.PI / 2;
        break;
      case 3:
        baseAngle = Math.PI;
        break;
    }

    const finalAngle = baseAngle + angle - Math.PI / 4;
    return {
      x: Math.cos(finalAngle) * distance,
      y: Math.sin(finalAngle) * distance,
    };
  }

  /**
   * Creates a burst of stars that animate from random edges of the given element.
   *
   * This asynchronous function generates a specified number of stars with random colors,
   * positions them at random edges of the element, and animates them to random end positions.
   * After the stars finish their animation, they will fade out and be removed from the DOM.
   *
   * @async
   * @param {HTMLElement} element - The DOM element to which the stars will be appended.
   * @param {number} count - The number of stars to create and animate.
   * @param {string[]} colors - An array of color strings to randomly assign to each star.
   *
   * @returns {Promise<void>} A promise that resolves when all animations are complete.
   *
   * @throws {Error} Throws an error if the element is not valid or if animations fail.
   *
   * @example
   * const container = document.getElementById('star-container');
   * const colors = ['#ff0000', '#00ff00', '#0000ff'];
   * createStarBurst(container, 10, colors)
   *   .then(() => console.log('Star burst animation completed!'))
   *   .catch((error) => console.error('Animation failed:', error));
   */
  createStarBurst(element, count, colors) {
    const rect = element.getBoundingClientRect();

    for (let i = 0; i < count; i++) {
      const color = colors[Math.floor(Math.random() * colors.length)];
      const star = this.createStar(color);
      const { position } = this.getRandomEdgePosition(rect);
      star.style.opacity = 0;
      star.style.left = `${position.x}px`;
      star.style.top = `${position.y}px`;
      element.appendChild(star);
    }

    const stars = element.querySelectorAll(".animated-star");

    console.log(stars);

    stars.forEach((star) => {
      const rotation = Math.random() * 360;
      const { edge } = this.getRandomEdgePosition(rect);
      const endPosition = this.getRandomEndPosition(edge);
      const sequence = [
        [
          star,
          { transform: ["rotate(0deg)", `translate(0px, 0px)`], opacity: 0 },
        ],
        [
          star,
          {
            transform: [
              `rotate(${rotation}deg)`,
              `translate(${endPosition.x}px, ${endPosition.y}px)`,
            ],
            opacity: 1,
          },
        ],
      ];
      animate(sequence, { ease: [0.39, 0.24, 0.3, 1], duration: 1 });
    });
  }

  removeStarBurst(element) {
    const stars = element.querySelectorAll(".animated-star");
    stars.forEach((star) => {
      animate(
        star,
        { transform: ["rotate(0deg)", `translate(0px, 0px)`], opacity: 0 },
        { duration: 0.5, easing: "ease-out" },
      ).then(() => star.remove());
    });
  }
}

export { StarHover };
